import { LOCALE_CODES, type Locale, localeMeta } from '@upweo/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Select,
  useI18n,
} from '@upweo/ui';
import type { StoreSettings } from '@upweo/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import { ApiClientError } from '../lib/api-client.js';
import * as api from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { PageHeader } from './PageHeader.js';
import { PaymentsCard } from './PaymentsCard.js';

export const SettingsPage = (): React.JSX.Element => {
  const { activeStore } = useAuth();
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery<StoreSettings>({
    queryKey: ['store', activeStore?.tenantId],
    queryFn: () => api.getStore(),
    enabled: Boolean(activeStore),
  });

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [defaultLocale, setDefaultLocale] = useState<Locale>('en');
  const [enabledLocales, setEnabledLocales] = useState<Locale[]>(['en']);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the form once the store loads, and again when the store changes.
  useEffect(() => {
    if (data) {
      setName(data.name);
      setSlug(data.slug);
      setCustomDomain(data.customDomain ?? '');
      setDefaultLocale(data.defaultLocale as Locale);
      setEnabledLocales(data.enabledLocales as Locale[]);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.updateStore({
        name,
        slug,
        // Empty means "clear it and fall back to the subdomain", which is null
        // in the column, not an empty string.
        customDomain: customDomain.trim() === '' ? null : customDomain.trim(),
        defaultLocale,
        enabledLocales,
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
      }, 2500);
      void queryClient.invalidateQueries({ queryKey: ['store'] });
      // The store name is shown in the switcher, so the session must reload too.
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: unknown) => {
      setSaved(false);
      setError(err instanceof ApiClientError ? err.error.message : 'Could not save settings');
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    save.mutate();
  };

  if (isPending || !data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="max-w-2xl space-y-5">
      <PageHeader title={t('settings.title')} subtitle={data.name} />

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.storefront')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <a
            href={data.storefrontUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary underline-offset-4 hover:underline"
          >
            {data.storefrontUrl}
          </a>
          <p className="text-xs text-muted-foreground">{t('settings.storefrontHint')}</p>
        </CardContent>
      </Card>

      <form onSubmit={onSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.details')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label={t('auth.storeName')} htmlFor="s-name">
              <Input
                id="s-name"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </Field>

            <Field
              label={t('auth.storeAddress')}
              htmlFor="s-slug"
              hint={t('settings.addressHint', { host: `${slug}.upweo.local` })}
            >
              <Input
                id="s-slug"
                required
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                }}
              />
            </Field>

            <Field
              label={t('settings.customDomain')}
              htmlFor="s-domain"
              hint={t('settings.customDomainHint')}
            >
              <Input
                id="s-domain"
                value={customDomain}
                placeholder="shop.example.com"
                onChange={(e) => {
                  setCustomDomain(e.target.value);
                }}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field label={t('settings.currency')} htmlFor="s-currency">
                <Input id="s-currency" value={data.currencyCode} disabled />
              </Field>
              <Field label={t('settings.country')} htmlFor="s-country">
                <Input id="s-country" value={data.countryCode} disabled />
              </Field>
              <Field label={t('settings.plan')} htmlFor="s-status">
                <Input id="s-status" value={data.status.toLowerCase()} disabled />
              </Field>
            </div>
            <p className="text-xs text-muted-foreground">{t('settings.currencyLocked')}</p>

            {error && (
              <p
                role="alert"
                className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </p>
            )}

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? t('common.saving') : t('common.save')}
              </Button>
              {saved && <span className="text-sm text-muted-foreground">{t('common.saved')}</span>}
            </div>
          </CardContent>
        </Card>
      </form>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.languages')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field
            label={t('settings.defaultLanguage')}
            htmlFor="s-locale"
            hint={t('settings.defaultLanguageHint')}
          >
            <Select
              id="s-locale"
              value={defaultLocale}
              onChange={(e) => {
                const next = e.target.value as Locale;
                setDefaultLocale(next);
                // The default must always be offered, or the storefront falls
                // back to a language it refuses to render.
                setEnabledLocales((current) =>
                  current.includes(next) ? current : [...current, next],
                );
              }}
            >
              {LOCALE_CODES.map((code) => (
                <option key={code} value={code} lang={code}>
                  {localeMeta(code).nativeName}
                </option>
              ))}
            </Select>
          </Field>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('settings.storefrontLanguages')}</legend>
            <p className="text-xs text-muted-foreground">{t('settings.storefrontLanguagesHint')}</p>
            <div className="grid gap-2 sm:grid-cols-3">
              {LOCALE_CODES.map((code) => {
                const isDefault = code === defaultLocale;
                return (
                  <label key={code} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input"
                      checked={enabledLocales.includes(code)}
                      /* The default cannot be unticked — see above. */
                      disabled={isDefault}
                      onChange={(e) => {
                        setEnabledLocales((current) =>
                          e.target.checked
                            ? [...new Set([...current, code])]
                            : current.filter((c) => c !== code),
                        );
                      }}
                    />
                    <span lang={code}>{localeMeta(code).nativeName}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              disabled={save.isPending}
              onClick={() => {
                save.mutate();
              }}
            >
              {save.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {activeStore && <PaymentsCard tenantId={activeStore.tenantId} />}
    </div>
  );
};
